import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../Components/ErrorPage";
import useDelete from "../../api/useDelete";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import AgentAdd from "../Agents/AgentAdd";
import AgentEdit from "../Agents/AgentEdit";
import AgentView from "../Agents/AgentView";

const SupplierIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [viewAgent, setViewAgent] = useState(null);
  const [editAgent, setEditAgent] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["suppliers", searchTerm, page, rowsPerPage],
    `shop/customers/?agent_type=supplier&search=${encodeURIComponent(searchTerm)}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete(
    "shop/customers",
    token,
    refetch
  );

  const handleAgentViewSuccess = (updatedAgent) => {
    refetch();
    if (updatedAgent?.id && viewAgent?.id && String(updatedAgent.id) === String(viewAgent.id)) {
      setViewAgent((current) =>
        current && String(current.id) === String(updatedAgent.id)
          ? { ...current, ...updatedAgent }
          : current
      );
    }
  };

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const agents = data?.results || data || [];
  const totalCount = data?.count || agents.length;

  if (isError) {
    return <ErrorPage message={t("agent.failedToLoadAgents", "Failed to load suppliers")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4">{t("agent.supplierListTitle", "Suppliers")}</Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
            {t("agent.dataLoadedOn", "Data loaded on")} {new Date().toLocaleString()}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("agent.addNewSupplier", "Add New Supplier")}
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("agent.searchSuppliers", "Search Suppliers")}
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="supplier table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("agent.id", "ID")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.agentName", "Supplier Name")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.agentNumber", "Phone Number")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.agentAddress", "Address")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.actions", "Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, index) => (
                  <TableRow key={`supplier-loading-${index}`}>
                    {["id", "name", "number", "address", "actions"].map((cell) => (
                      <TableCell key={cell}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : agents.map((agent, index) => (
                  <TableRow key={agent.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{agent.name || "-"}</TableCell>
                    <TableCell>{agent.contact || "-"}</TableCell>
                    <TableCell>{agent.address || "-"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => setViewAgent(agent)}
                        >
                          {t("agent.view", "View")}
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => setEditAgent(agent)}
                        >
                          {t("agent.edit", "Edit")}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(agent.id)}
                        >
                          {t("agent.delete", "Delete")}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("agent.rowsPerPage", "Rows per page:")}</Typography>
          <FormControl
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { border: "none" },
              },
            }}
          >
            <Select
              value={rowsPerPage}
              onChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(1);
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage) || 1}
          page={page}
          onChange={(_, nextPage) => setPage(nextPage)}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      <ConfirmDialog />

      {openAdd && (
        <AgentAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
          agentType="supplier"
          onSuccess={refetch}
        />
      )}
      {editAgent && (
        <AgentEdit
          agent={editAgent}
          open={Boolean(editAgent)}
          onClose={() => setEditAgent(null)}
          token={token}
          onSuccess={refetch}
        />
      )}
      {viewAgent && (
        <AgentView
          agent={viewAgent}
          open={Boolean(viewAgent)}
          onClose={() => setViewAgent(null)}
          token={token}
          onSuccess={handleAgentViewSuccess}
        />
      )}
    </Box>
  );
};

export default SupplierIndex;
